import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBolletta, downloadBollettaFile } from "@/api/bollette";
import { ApiError } from "@/api/client";
import { PraticheSkeleton } from "@/components/PraticheSkeleton";
import { ErrorState } from "@/components/ErrorState";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/Badge";
import { TipoIcon } from "@/components/TipoIcon";
import { TIPO_BADGE } from "@/lib/constants";
import { ArrowLeft, Search, Download } from "lucide-react";
import { formatDateTime } from "@/lib/dates";
import type { BollettaApiDetail } from "@/types";

export function formatAddress(addr: BollettaApiDetail["indirizzoFornitura"]): string {
  const parts = [addr.street, addr.postalCode, addr.city, addr.state, addr.country].filter(Boolean);
  const base = parts.join(", ") || "—";
  return addr.interno ? `${base} (${addr.interno})` : base;
}

export function DettaglioBolletta() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bolletta, setBolletta] = useState<BollettaApiDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    setError(null);
    getBolletta(id)
      .then(setBolletta)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
        } else if (e instanceof ApiError && e.status >= 500) {
          setError("Errore del sistema. Riprova tra qualche istante.");
        } else {
          setError("Impossibile caricare la bolletta.");
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PraticheSkeleton />;

  if (notFound) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/bollette")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
        >
          <ArrowLeft size={16} /> Torna alle bollette
        </button>
        <EmptyState icon={<Search size={26} className="text-brand" />} title="Bolletta non trovata" />
      </div>
    );
  }

  if (error) return <ErrorState message={error} />;
  if (!bolletta) return null;

  const tipoBadge = TIPO_BADGE[bolletta.tipoFornitura] ?? { label: bolletta.tipoFornitura, bg: "bg-gray-100", text: "text-gray-800" };

  function handleDownload() {
    if (!bolletta) return;
    downloadBollettaFile(bolletta.id, bolletta.fileName || bolletta.name).catch((err: Error) => {
      showToast(err.message ?? "Errore download");
    });
  }

  return (
    <div className="space-y-4 max-w-2xl">
      {toast && (
        <div className="bg-red-100 text-red-800 border border-red-200 px-4 py-3 rounded-lg text-sm fixed bottom-6 right-6 z-50 shadow-lg">
          {toast}
        </div>
      )}

      {/* Breadcrumb */}
      <button
        onClick={() => navigate("/bollette")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
      >
        <ArrowLeft size={16} /> Bollette / <span className="text-text font-medium">{bolletta.name}</span>
      </button>

      {/* Card 1 — Riepilogo */}
      <div className="bg-card p-6 rounded-lg border border-border">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text">{bolletta.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge bg={tipoBadge.bg} text={tipoBadge.text} className="gap-1"><TipoIcon tipo={bolletta.tipoFornitura} />{tipoBadge.label}</Badge>
              <span className="text-sm text-muted-foreground">{formatDateTime(bolletta.dataBolletta)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-text">€{bolletta.importoMensile.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">al mese</div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleDownload}
            disabled={!bolletta.hasFile}
            className="bg-brand text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
          >
            <Download size={14} /> Scarica bolletta
          </button>
        </div>
      </div>

      {/* Card 2 — Fornitura */}
      <div className="bg-card p-6 rounded-lg border border-border">
        <h3 className="font-semibold text-text mb-3">Fornitura</h3>
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-muted-foreground">Fornitore attuale: </span>
            <span className="text-text font-medium">{bolletta.fornitoreAttuale}</span>
          </div>
          {bolletta.codicePod && (
            <div>
              <span className="text-muted-foreground">POD: </span>
              <span className="text-text font-mono">{bolletta.codicePod}</span>
            </div>
          )}
          {bolletta.codicePdr && (
            <div>
              <span className="text-muted-foreground">PDR: </span>
              <span className="text-text font-mono">{bolletta.codicePdr}</span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Indirizzo fornitura: </span>
            <span className="text-text">{formatAddress(bolletta.indirizzoFornitura)}</span>
          </div>
        </div>
      </div>

      {/* Card 3 — Consumi */}
      <div className="bg-card p-6 rounded-lg border border-border">
        <h3 className="font-semibold text-text mb-3">Consumi</h3>
        <div className="space-y-2 text-sm">
          {bolletta.kwhAnnui > 0 && (
            <div>
              <span className="text-muted-foreground">Consumo annuo (elettricità): </span>
              <span className="text-text font-medium">
                {bolletta.kwhAnnui.toLocaleString("it-IT")} kWh
                {bolletta.consumoStimato && <span className="text-muted-foreground"> (stima)</span>}
              </span>
            </div>
          )}
          {bolletta.smcAnnui > 0 && (
            <div>
              <span className="text-muted-foreground">Consumo annuo (gas): </span>
              <span className="text-text font-medium">
                {bolletta.smcAnnui.toLocaleString("it-IT")} Smc
                {bolletta.consumoStimato && <span className="text-muted-foreground"> (stima)</span>}
              </span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Importo mensile: </span>
            <span className="text-text font-semibold">€{bolletta.importoMensile.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Card 4 — Note (conditional) */}
      {bolletta.note && (
        <div className="bg-card p-6 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Note</h3>
          <p className="text-sm text-text whitespace-pre-wrap">{bolletta.note}</p>
        </div>
      )}
    </div>
  );
}
